import { Sync as SyncIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import JalaliDatePicker from "../../components/shared/jalali-datepicker";
import { formatApiDate } from "../SLN/market-growth/utils/commissionValidation";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import { copyToClipboard } from "./utils/portalHelpers";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const DEFAULT_FILTERS = {
  sourcePartnerId: "",
  fromDate: null,
  toDate: null,
};

const PartnerRegisteredWithoutProjectPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const buildParams = useCallback(() => {
    const params = { page: page + 1, pageSize };
    if (filters.sourcePartnerId) params.sourcePartnerId = filters.sourcePartnerId;
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters, page, pageSize]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    setIsUnauthorized(false);
    try {
      const res = await PartnerCommissionApi.getRegisteredWithoutProject(buildParams());
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری گزارش ناموفق بود."
      );

      if (normalized.noPartnerProfile) {
        setNoPartnerProfile(true);
        setPageData(null);
        return;
      }

      if (normalized.isUnauthorized) {
        setIsUnauthorized(true);
        setPageData(null);
        return;
      }

      if (normalized.error) {
        setError(normalized.error);
        setPageData(null);
        return;
      }

      setPageData(normalized.data || null);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const items = Array.isArray(pageData?.items) ? pageData.items : [];
  const sourceOptions = Array.isArray(pageData?.sourcePartnerOptions)
    ? pageData.sourcePartnerOptions
    : [];
  const totalCount = pageData?.totalCount ?? 0;

  const applyFilters = () => {
    setPage(0);
    loadReport();
  };

  if (isUnauthorized) {
    return (
      <PartnerCommissionPageShell
        title="ثبت‌نام بدون درخواست"
        description="این گزارش فقط برای مدیران سطح یک در دسترس است."
        loading={false}
        error={null}
        noPartnerProfile={false}
      >
        <Alert severity="warning">
          دسترسی به این گزارش فقط برای مدیر بازاریابی سطح یک مجاز است.
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button component={RouterLink} to="/SLN/Partner/PartnerLeads" variant="outlined">
            بازگشت به گزارش لیدها
          </Button>
        </Box>
      </PartnerCommissionPageShell>
    );
  }

  return (
    <PartnerCommissionPageShell
      title="ثبت‌نام بدون درخواست نیروگاه"
      description="متقاضیانی که از طریق لینک معرفی وارد شده و ثبت‌نام کرده‌اند، اما هنوز درخواست نیروگاه ثبت نکرده‌اند."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={loadReport}
      empty={false}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        منبع ورود هر متقاضی (لینک خودتان یا زیرمجموعه) و آدرس لندینگ در جدول زیر نمایش داده
        می‌شود.
      </Alert>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نماینده معرف</InputLabel>
                <Select
                  label="نماینده معرف"
                  value={filters.sourcePartnerId}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, sourcePartnerId: e.target.value }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {sourceOptions.map((opt) => (
                    <MenuItem key={opt.partnerId} value={String(opt.partnerId)}>
                      {opt.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="contained" onClick={applyFilters} fullWidth>
                اعمال فیلتر
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip title="بارگذاری مجدد">
                <IconButton onClick={loadReport} aria-label="بارگذاری مجدد">
                  <SyncIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ overflowX: "auto", p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>کد لید</TableCell>
                <TableCell>موبایل</TableCell>
                <TableCell>نماینده معرف</TableCell>
                <TableCell>منبع ورود</TableCell>
                <TableCell>لینک معرفی</TableCell>
                <TableCell>آدرس ورود</TableCell>
                <TableCell>تاریخ ثبت‌نام</TableCell>
                <TableCell>اولین بازدید</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      متقاضی ثبت‌نام‌شده بدون درخواست یافت نشد.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {items.map((row) => (
                <TableRow key={row.leadCode} hover>
                  <TableCell>{row.leadCode}</TableCell>
                  <TableCell dir="ltr" sx={{ textAlign: "right" }}>
                    {row.mobile || row.maskedMobile || "—"}
                  </TableCell>
                  <TableCell>{row.sourcePartnerDisplayName || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.isFromSubPartner ? "زیرمجموعه" : "لینک خودم"}
                      color={row.isFromSubPartner ? "default" : "primary"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    {row.referralLink ? (
                      <Tooltip title={row.referralLink}>
                        <Button
                          size="small"
                          onClick={() => copyToClipboard(row.referralLink, "لینک کپی شد.")}
                        >
                          کپی لینک
                        </Button>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap title={row.landingUrl || ""}>
                      {row.landingUrl || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(row.registeredAt)}</TableCell>
                  <TableCell>{formatDate(row.firstSeenAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="تعداد در صفحه"
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 2 }}>
        <Button component={RouterLink} to="/SLN/Partner/PartnerLeads" variant="text">
          بازگشت به گزارش لیدها
        </Button>
      </Box>
    </PartnerCommissionPageShell>
  );
};

export default PartnerRegisteredWithoutProjectPage;
